import React, {
  DependencyList,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

export const DASHBOARD_NAV_EXPANDED_WIDTH = 304;
export const DASHBOARD_NAV_COLLAPSED_WIDTH = 76;

const DASHBOARD_NAV_STORAGE_KEY = 'fable-dashboard-navigation-open';

const getStoredNavigationOpen = () => {
  if (typeof window === 'undefined') return true;
  try {
    const stored = window.localStorage.getItem(DASHBOARD_NAV_STORAGE_KEY);
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
  } catch (error) {
    console.warn('Unable to read dashboard navigation preference', error);
  }
  return true;
};

export type DashboardHeaderBack = {
  label?: string;
  onClick: () => void;
};

export type DashboardHeaderConfig = {
  title: ReactNode;
  subtitle?: ReactNode;
  align?: 'left' | 'center';
  back?: DashboardHeaderBack;
  actions?: ReactNode;
};

type DashboardShellContextValue = {
  header: DashboardHeaderConfig | null;
  setHeader: React.Dispatch<React.SetStateAction<DashboardHeaderConfig | null>>;
  leftRailWidth: number;
  setLeftRailWidth: React.Dispatch<React.SetStateAction<number>>;
  navigationOpen: boolean;
  setNavigationOpen: (open: boolean) => void;
};

const DashboardShellContext = createContext<DashboardShellContextValue | null>(null);

export const DashboardShellProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [header, setHeader] = useState<DashboardHeaderConfig | null>(null);
  const [leftRailWidth, setLeftRailWidth] = useState(0);
  const { navigationOpen, setNavigationOpen } = useDashboardNavigationPreference();

  const value = useMemo(
    () => ({
      header,
      setHeader,
      leftRailWidth,
      setLeftRailWidth,
      navigationOpen,
      setNavigationOpen,
    }),
    [header, leftRailWidth, navigationOpen, setNavigationOpen],
  );

  return (
    <DashboardShellContext.Provider value={value}>
      {children}
    </DashboardShellContext.Provider>
  );
};

/** Keep the same navigation preference in the dashboard and standalone Studio. */
export const useDashboardNavigationPreference = () => {
  const [navigationOpen, setNavigationOpenState] = useState(getStoredNavigationOpen);

  const setNavigationOpen = useCallback((open: boolean) => {
    setNavigationOpenState(open);
    try {
      window.localStorage.setItem(DASHBOARD_NAV_STORAGE_KEY, open ? 'open' : 'closed');
    } catch (error) {
      console.warn('Unable to save dashboard navigation preference', error);
    }
  }, []);

  return { navigationOpen, setNavigationOpen };
};

export const useDashboardShell = () => {
  const context = useContext(DashboardShellContext);
  if (!context) {
    throw new Error('useDashboardShell must be used inside DashboardShellProvider');
  }
  return context;
};

export const useDashboardHeader = (
  config: DashboardHeaderConfig,
  dependencies: DependencyList,
) => {
  const { setHeader } = useDashboardShell();

  useLayoutEffect(() => {
    setHeader(config);
    return () => {
      setHeader((current) => (current === config ? null : current));
    };
    // Callers provide the values that materially change their header.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setHeader, ...dependencies]);
};

export const useDashboardLeftRail = (width: number) => {
  const { setLeftRailWidth } = useDashboardShell();

  useLayoutEffect(() => {
    setLeftRailWidth(width);
    return () => setLeftRailWidth(0);
  }, [setLeftRailWidth, width]);
};
